import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '@/App.css';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import CaseStudyDetailPage from './pages/CaseStudyDetailPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import { Menu, X, Instagram, Linkedin, Facebook, Phone, MessageCircle, ArrowRight, Mail } from 'lucide-react';

// Smooth scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

// Salient-style Navigation
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show/hide based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      
      // Add background on scroll
      setScrolled(currentScrollY > 50);
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Work', path: '/case-studies' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav 
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' 
            : 'bg-gray-900/90 backdrop-blur-md py-4'
        } ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img 
                src={scrolled ? "/logo-circle-blue.png" : "/logo-circle-white.png"}
                alt="Zenith Marketing" 
                className="w-12 h-12 object-contain hover:scale-105 transition-transform duration-200"
              />
            </Link>

            {/* Center Navigation - Desktop */}
            <div className="hidden lg:flex items-center gap-x-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`px-5 py-2.5 text-[15px] font-semibold uppercase tracking-wide transition-all duration-200 ${
                    isActive(link.path)
                      ? 'text-brand-blue'
                      : scrolled 
                        ? 'text-gray-800 hover:text-brand-blue' 
                        : 'text-white hover:text-brand-blue'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Link 
                to="/contact" 
                data-testid="nav-cta-btn"
                className="px-7 py-3.5 bg-gradient-to-r from-brand-blue to-cyan-500 text-white text-sm font-bold uppercase tracking-wide rounded-full hover:from-cyan-500 hover:to-brand-blue transition-all duration-300 inline-flex items-center shadow-lg shadow-brand-blue/25"
              >
                LET'S TALK
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? (
                <X size={24} className="text-gray-900" />
              ) : (
                <Menu size={24} className="text-gray-900" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div 
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 lg:hidden shadow-2xl"
            >
              <div className="p-6 h-full flex flex-col">
                <div className="flex justify-end mb-8">
                  <motion.button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 rounded-xl hover:bg-gray-100"
                    whileTap={{ scale: 0.95 }}
                  >
                    <X size={24} />
                  </motion.button>
                </div>
                
                <div className="flex-1 space-y-2">
                  {navLinks.map((link, idx) => (
                    <motion.div
                      key={link.path}
                      initial={{ x: 50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block px-4 py-4 text-xl font-medium rounded-xl transition-all ${
                          isActive(link.path)
                            ? 'text-brand-blue bg-brand-blue/5'
                            : 'text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
                
                <motion.div 
                  className="pt-6 border-t border-gray-100"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                    <button className="w-full bg-brand-blue text-white px-6 py-4 rounded-xl font-semibold hover:bg-brand-blue-hover transition-all flex items-center justify-center">
                      Let's Talk
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Salient-style Footer - Light Grey Gradient
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-gradient-to-b from-gray-100 to-gray-200 text-gray-800">
      {/* Main CTA Section */}
      <div className="border-b border-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-outfit text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Let's Work
              <span className="block bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent mt-2">Together</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-xl mx-auto mb-10">
              Ready to elevate your brand? Let's create something extraordinary.
            </p>
            <Link 
              to="/contact" 
              data-testid="footer-cta-btn"
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-semibold text-lg rounded-full hover:from-cyan-400 hover:to-brand-blue transition-all duration-300 shadow-xl shadow-brand-blue/30"
            >
              Start a Project
              <ArrowRight className="ml-3 w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-5">
              <img 
                src="/logo-circle-blue.png" 
                alt="Zenith Marketing Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="ml-3 text-lg font-semibold tracking-tight">Zenith Marketing</span>
            </div>
            <p className="text-gray-600 leading-relaxed mb-5 max-w-sm text-sm">
              A boutique marketing agency specialising in Social Media Management, Branding, Digital Marketing Strategy & Website Design.
            </p>
            <div className="space-y-2 text-gray-600 text-sm">
              <p>D-10, S2, Airport Enclave</p>
              <p>Jagatpura, Jaipur, Rajasthan</p>
              <div className="flex items-center gap-4 mt-3">
                <a href="tel:+919928279319" className="flex items-center gap-2 hover:text-brand-blue transition-colors">
                  <Phone size={16} className="text-brand-blue" />
                  <span>+91 9928279319</span>
                </a>
                <a 
                  href="https://wa.me/919928279319" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-500 transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle size={18} />
                </a>
              </div>
              <a href="mailto:info@zenithmarketingindia.com" className="flex items-center gap-2 hover:text-brand-blue transition-colors">
                <Mail size={16} className="text-brand-blue" />
                <span>info@zenithmarketingindia.com</span>
              </a>
            </div>

            {/* Social Media */}
            <div className="flex gap-3 mt-6">
              {[
                { icon: Instagram, href: "https://www.instagram.com/zenithmarketingindia/", id: "footer-instagram" },
                { icon: Linkedin, href: "https://www.linkedin.com/company/zenithmarketingindia/", id: "footer-linkedin" },
                { icon: Facebook, href: "https://www.facebook.com/zenithmarketingindia/", id: "footer-facebook" }
              ].map(({ icon: Icon, href, id }) => (
                <a 
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={id}
                  className="w-10 h-10 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center hover:bg-gradient-to-r hover:from-brand-blue hover:to-cyan-400 hover:text-white hover:border-transparent transition-all duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Navigation</h3>
            <ul className="space-y-3">
              {['Home', 'Services', 'Work', 'Blog', 'About', 'Contact'].map((item) => (
                <li key={item}>
                  <Link 
                    to={item === 'Home' ? '/' : item === 'Work' ? '/case-studies' : `/${item.toLowerCase()}`} 
                    className="text-gray-600 hover:text-brand-blue transition-colors text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Services</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li>Social Media Management</li>
              <li>Performance Marketing</li>
              <li>Website Development</li>
              <li>Brand Strategy</li>
              <li>Content Creation</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-300 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-xs">
            © {currentYear} Zenith Marketing India. All rights reserved.
          </p>
          <Link to="/privacy-policy" className="text-gray-500 text-xs hover:text-brand-blue transition-colors">
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/919928279319?text=Hi%20Zenith%20Marketing!%20I%27d%20like%20to%20discuss%20a%20project."
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200"
    aria-label="Chat on WhatsApp"
    data-testid="whatsapp-float-btn"
  >
    <MessageCircle className="text-white" size={28} fill="white" />
  </a>
);

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500);
    };
    
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  if (!isVisible) return null;
  
  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 left-6 z-40 w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-brand-blue transition-colors duration-200"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App min-h-screen flex flex-col font-poppins bg-white">
        <Navigation />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
        <BackToTop />
      </div>
    </BrowserRouter>
  );
}

export default App;
